"use client"

import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Label,
  Textarea,
  cn,
} from '@yeshuman/sdk'
import { useState, type MouseEvent } from 'react'
import { ClipboardCheck } from 'lucide-react'
import { toast } from 'sonner'
import {
  INTERVIEW_OUTCOME_NOTES_MAX,
  useSetCandidateOutcome,
  type InterviewOutcome,
  type InterviewOutcomeState,
} from '../../hooks/use-briefs'
import { INTERVIEW_OUTCOME_OPTIONS, interviewOutcomeOption } from '../../lib/interview-outcome-display'

export type BriefCandidateOutcomeControlProps = {
  briefId: string
  profileId: string
  candidateName: string
  /** Job the brief is linked to; outcomes are recorded against it. Null disables the control. */
  jobTitle?: string | null
  hasLinkedJob: boolean
  outcome?: InterviewOutcome | null
  size?: 'sm' | 'default'
  className?: string
}

const stopClick = (event: MouseEvent) => event.stopPropagation()

/** Footer action on a saved-candidate card: record or replace the interview outcome. */
export function BriefCandidateOutcomeControl({
  briefId,
  profileId,
  candidateName,
  jobTitle,
  hasLinkedJob,
  outcome,
  size = 'sm',
  className,
}: BriefCandidateOutcomeControlProps) {
  const [open, setOpen] = useState(false)
  const [state, setState] = useState<InterviewOutcomeState | null>(outcome?.state ?? null)
  const [notes, setNotes] = useState(outcome?.notes ?? '')
  const { setOutcome, isSettingOutcome } = useSetCandidateOutcome()

  const current = interviewOutcomeOption(outcome?.state)
  const notesTooLong = notes.length > INTERVIEW_OUTCOME_NOTES_MAX

  const handleOpenChange = (next: boolean) => {
    if (next) {
      setState(outcome?.state ?? null)
      setNotes(outcome?.notes ?? '')
    }
    setOpen(next)
  }

  const handleSave = () => {
    if (!state) {
      toast.error('Choose an outcome')
      return
    }
    if (notesTooLong) {
      toast.error(`Notes must be ${INTERVIEW_OUTCOME_NOTES_MAX} characters or fewer`)
      return
    }
    setOutcome(
      { briefId, profileId, data: { state, notes: notes.trim() } },
      {
        onSuccess: (saved) => {
          toast.success(`${candidateName}: ${saved.state_label}`)
          setOpen(false)
        },
        onError: (error: Error) => {
          toast.error(error.message || 'Failed to record outcome')
        },
      },
    )
  }

  const triggerLabel = current ? current.label : 'Record outcome'
  const disabledReason = hasLinkedJob ? undefined : 'Link a job to this brief to record outcomes'

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button
          type="button"
          size={size}
          variant={current ? 'secondary' : 'outline'}
          className={cn('gap-1.5', className)}
          disabled={!hasLinkedJob}
          title={disabledReason}
          aria-label={`${triggerLabel} for ${candidateName}`}
          onClick={stopClick}
          data-testid="brief-candidate-outcome-trigger"
        >
          <ClipboardCheck className="size-4 shrink-0" aria-hidden />
          {triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent onClick={stopClick} data-testid="brief-candidate-outcome-dialog">
        <DialogHeader>
          <DialogTitle>Interview outcome</DialogTitle>
          <DialogDescription>
            {jobTitle
              ? `What happened with ${candidateName} for ${jobTitle}? Saving again replaces the current outcome.`
              : `What happened with ${candidateName}? Saving again replaces the current outcome.`}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Outcome</Label>
            <div role="radiogroup" aria-label="Outcome" className="grid gap-2 sm:grid-cols-2">
              {INTERVIEW_OUTCOME_OPTIONS.map((option) => {
                const selected = option.value === state
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    data-testid={`outcome-option-${option.value}`}
                    onClick={() => setState(option.value)}
                    className={cn(
                      'flex flex-col items-start gap-1 rounded-md border px-3 py-2 text-left text-sm transition-colors',
                      'hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      selected ? 'border-primary bg-primary/5' : 'border-border',
                    )}
                  >
                    <Badge variant={option.badgeVariant} className="text-xs font-medium">
                      {option.label}
                    </Badge>
                    <span className="text-xs text-muted-foreground">{option.description}</span>
                  </button>
                )
              })}
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor={`outcome-notes-${profileId}`}>Notes (optional)</Label>
              <span
                className={cn('text-xs tabular-nums', notesTooLong ? 'text-destructive' : 'text-muted-foreground')}
                aria-live="polite"
              >
                {notes.length}/{INTERVIEW_OUTCOME_NOTES_MAX}
              </span>
            </div>
            <Textarea
              id={`outcome-notes-${profileId}`}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={INTERVIEW_OUTCOME_NOTES_MAX}
              placeholder={state === 'failed' ? 'Why did it not work out?' : 'Anything the client said…'}
              rows={3}
              data-testid="brief-candidate-outcome-notes"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              disabled={isSettingOutcome || !state || notesTooLong}
              data-testid="brief-candidate-outcome-save"
            >
              {isSettingOutcome ? 'Saving…' : 'Save outcome'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
